import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import httpClient from '../httpClient';
import { paperListOrder, normalizePaperName, COMING_SOON_ALBUM_TYPES } from '../lib/const';
import { dollarTemplate, apiErrorMessage, filterAlbumProductTypePhotosForLineItem, getAlbumLineItemPhotoValidationError, getAlbumSpreadError, filterOutMultiOpeningPrintSizes, filterOutSpecialProductPrintSizes} from '../lib/utils';
import sanitize from 'sanitize-filename';
import useAlbumSelectionStore from './useAlbumSelectionStore';
import useMultiOpeningMatStore from './useMultiOpeningMatStore';

const isEmptyGuidish = (v) =>
  v === undefined || v === null || v === '' || v === 'default' || v === 'none';

const CIRCLE_OVAL_FRAME_PATTERN = /\b(Circle|Oval)\s+Frame\b/i;
const isCircleOrOvalFrameType = (text) => CIRCLE_OVAL_FRAME_PATTERN.test(text || '');

const FRAMED_CANVAS_API_NAME = 'Canvas - White Wrap - 0.75 inch bar';
const FRAMED_CANVAS_DISPLAY_NAME = 'Framed Canvas';
const isFramedCanvasPaper = (paperName) =>
  [FRAMED_CANVAS_API_NAME, FRAMED_CANVAS_DISPLAY_NAME].includes(paperName);

// Circle/Oval finishing services always report frame_mat_name as "no mat" -
// there is no color mat for these frames. The only real Frame Mat dimension
// is mat type (8-ply vs double 4-ply), which is encoded in the tail of the
// pipe-joined service name (e.g. "... Oval Frame | 8-Ply mat"). These patterns
// let us derive the canonical products/frame_mats mat_type key from that text
// so selection can be matched against real backend mat_type values instead of
// the raw compound name string.
const CIRCLE_OVAL_MAT_TYPE_PATTERNS = {
  'no-mat': /no[\s-]*mat/i,
  '8-ply': /8[\s-]*ply/i,
  'double-mat-4-ply': /double.*4[\s-]*ply/i,
};

// Mirrors the products/frame_mats mat_type values / labels used by
// FramingFormInputs.jsx's Mat Type menu.
const MAT_TYPE_LABELS = {
  'no-mat': 'No Mat',
  '8-ply': '8-Ply',
  'double-mat-4-ply': 'Double Mat 4-Ply',
};

const getCircleOvalServiceMatNameSuffix = (service) => {
  const nameParts = service?.attributes?.name?.split('|');
  if (nameParts && nameParts.length > 1) {
    return nameParts[nameParts.length - 1].trim();
  }
  return service?.attributes?.frameMatName;
};

const deriveCircleOvalMatTypeKey = (matNameSuffix) => {
  if (!matNameSuffix) return undefined;
  return Object.keys(CIRCLE_OVAL_MAT_TYPE_PATTERNS).find((key) =>
    CIRCLE_OVAL_MAT_TYPE_PATTERNS[key].test(matNameSuffix),
  );
};

const getFloatCanvasFrameName = (service) => {
  const desc = service.attributes.description || '';
  const shortName = desc.split(' for a')[0].replace(': ', ' - ');
  return shortName || desc;
};

const resolveAlbumSelectableId = (inputVal, metaVal) => {
  if (inputVal === 'none' || inputVal === 'default') return undefined;
  if (!isEmptyGuidish(inputVal)) return inputVal;
  if (!isEmptyGuidish(metaVal)) return metaVal;
  return undefined;
};

const buildAlbumCoverTextProperties = (inputs) => {
  const cover = (inputs?.coverText?.selectedValue || '').trim();
  const top = (inputs?.debossingTextTop?.selectedValue || '').trim();
  const bottom = (inputs?.debossingTextBottom?.selectedValue || '').trim();
  if (!cover && !top && !bottom) return [];

  const entry = {};

  if (cover) {
    entry.cover_text = { cover_text_value: cover };
  }
  if (top) {
    entry.upper_text = { upper_text_value: top };
  }
  if (bottom) {
    entry.lower_text = { lower_text_value: bottom };
  }

  return [entry];
};

const buildAlbumLineItemRequestBody = (
  albumForm,
  orderedPhotos,
  notesValue,
  isUpdate = false,
) => {
  const inputs = albumForm?.inputs || {};
  const meta = albumForm?.meta || {};
  const spreadRaw = inputs.numberOfSpreads?.selectedValue;
  const spreadParsed = parseInt(spreadRaw, 10);
  const baseSpreads = albumForm?.meta?.baseSpreads || 10;
  const spread_quantity = Number.isFinite(spreadParsed) && spreadParsed > 0 ? spreadParsed : baseSpreads;

  const photoList = Array.isArray(orderedPhotos) ? orderedPhotos : [];
  const photoError = getAlbumLineItemPhotoValidationError(photoList, isUpdate);
  if (photoError) {
    return { error: photoError };
  }

  const albumPhotoList = filterAlbumProductTypePhotosForLineItem(photoList);

  const album_photos = albumPhotoList
    .map((photo) => (photo.attributes || {}).guid)
    .filter((guid) => !isEmptyGuidish(guid));

  const album = {
    ...(album_photos.length ? { album_photos } : {}),
    product_type: 'album',
    album_type_id: inputs.albumType?.selectedValue,
    album_paper_id: inputs.albumPaper?.selectedValue,
    album_size_price_id: inputs.albumSize?.selectedValue,
    inner_fabric_id: inputs.interiorFabric?.selectedValue,
    outer_fabric_id: inputs.coverFabric?.selectedValue,
    spread_quantity,
    notes: (notesValue || '').trim(),
    font_option_id: resolveAlbumSelectableId(inputs.fontOption?.selectedValue, meta.fontOptionId),
    pre_designed_cover_option_id: resolveAlbumSelectableId(
      inputs.preDesignedCover?.selectedValue,
      meta.preDesignedCoverId,
    ),
    stamping_id: inputs.debossingType?.selectedValue,
    cover_text_properties: buildAlbumCoverTextProperties(inputs),
  };

  Object.keys(album).forEach((key) => {
    const val = album[key];
    if (key === 'album_photos' || key === 'spread_quantity' || key === 'cover_text_properties') return;
    if (typeof val === 'string' && val.trim() === '') {
      delete album[key];
      return;
    }
    if (isEmptyGuidish(val)) delete album[key];
  });

  const upgrade = inputs.clamshellUpgrade?.selectedValue;
  let clamshell;
  if (upgrade === 'yes') {
    clamshell = {
      outer_fabric_id: inputs.clamshellOutsideFabric?.selectedValue,
      tray_fabric_id: inputs.clamshellTrayFabric?.selectedValue,
      stamping_id: inputs.clamshellDebossingType?.selectedValue,
      pre_designed_cover_option_id: resolveAlbumSelectableId(
        inputs.clamshellPreDesignedCover?.selectedValue,
        meta.clamshellPreDesignedCoverId,
      ),
      font_option_id: resolveAlbumSelectableId(
        inputs.clamshellFontOption?.selectedValue,
        meta.clamshellFontOptionId,
      ),
      cover_text: (inputs.clamshellCoverText?.selectedValue || '').trim(),
    };
    Object.keys(clamshell).forEach((k) => {
      if (k === 'clamshell_upgrade' || k === 'cover_text') return;
      if (isEmptyGuidish(clamshell[k])) delete clamshell[k];
    });
  } else {
    clamshell = null;
  }

  return {
    line_item: {
      ...album,
      ...(clamshell ? { clamshell } : {}),
    }
  }
}

// image sorting
const sortPhotosByName = (photos) => {
  if (!Array.isArray(photos)) return photos;
  return [...photos].sort((a, b) => {
    const nameA = a?.attributes?.photoFileName || '';
    const nameB = b?.attributes?.photoFileName || '';
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const useProductSelectionStore = create(
  persist(
    (set, get) => ({
  photos: [],
  setPhotos: (photos) => set({ photos: sortPhotosByName(photos) }),
  isResetting: false,

  orderId: '',
  setOrderId: (orderId) => set({ orderId }),

  selectedPhoto: '',
  setSelectedPhoto: (selectedPhoto) => set({ selectedPhoto }),

  selectAllPhotos: false,
  setSelectAllPhotos: (selectAllPhotos) => set({ selectAllPhotos }),

  photosLoadingMessage: '',
  setPhotosLoadingMessage: (photosLoadingMessage) => set({ photosLoadingMessage }),

  photographGuid: '',
  setPhotographGuid: (photographGuid) => set({ photographGuid }),

  addToCart: false,
  setAddToCart: (addToCart) => set({ addToCart }),

  apiError: null,
  setApiError: (apiError) => set({ apiError }),

  cartCount: 0,
  setCartCount: (cartCount) => set({ cartCount }),

  imageCartCount: 0,
  setImageCartCount: (imageCartCount) => set({ imageCartCount }),

  subtotal: 0,
  setSubtotal: (subtotal) => set({ subtotal }),

  ModalIsOpen: false,
  setModalIsOpen: (ModalIsOpen) => set({ ModalIsOpen }),

  lineItem: '',
  setLineItem: (lineItem) => set({ lineItem }),

  isEditInitializing: false,
  setIsEditInitializing: (isEditInitializing) => set({ isEditInitializing }),

  isSuccessModalOpen: false,
  setIsSuccessModalOpen: (isSuccessModalOpen) => set({ isSuccessModalOpen }),

  successModalMessage: '',
  setSuccessModalMessage: (successModalMessage) => set({ successModalMessage }),

  uploadsPreview: [],
  setUploadsPreview: (uploadsPreview) => set({ uploadsPreview }),

  addPreview: (preview) =>
    set((state) => ({ uploadsPreview: [...state.uploadsPreview, preview] })),

  removePreview: (tempId) =>
    set((state) => ({
      uploadsPreview: state.uploadsPreview.filter((img) => img.id !== tempId),
    })),

  notes: { _all: '' },
  setNotes: (notesOrUpdater) => {
    if (typeof notesOrUpdater === 'function') {
      set((state) => ({ notes: notesOrUpdater(state.notes) }));
    } else {
      set({ notes: notesOrUpdater });
    }
  },

  productType: 'default',
  setProductType: (productType) => set({ productType, selectAllPhotos: false }),

  finishingCategoriesData: null,
  setFinishingCategoriesData: (data) => set({ finishingCategoriesData: data }),

  finishingServicesData: null,
  setFinishingServicesData: (data) => set({ finishingServicesData: data }),

  // Query loading states
  queryStates: {
    orderPhoto: { isLoading: false, isError: false, isSuccess: false },
    photos: { isLoading: false, isError: false, isSuccess: false },
    labOrder: { isLoading: false, isError: false, isSuccess: false },
    paperTypes: { isLoading: false, isError: false, isSuccess: false },
    printSizes: { isLoading: false, isError: false, isSuccess: false },
    finishingCategories: { isLoading: false, isError: false, isSuccess: false },
    finishingServices: { isLoading: false, isError: false, isSuccess: false },
    finishingDetails: { isLoading: false, isError: false, isSuccess: false },
  },
  setQueryState: (queryName, state) => set((prev) => ({
    queryStates: {
      ...prev.queryStates,
      [queryName]: { ...prev.queryStates[queryName], ...state },
    },
  })),

  // Form state
  defaultOptions: {
    meta: {
      subtotal: 0,
      xAspectRatio: undefined,
      yAspectRatio: undefined,
      aspectRatio: undefined,
      cropPositionX: undefined,
      cropPositionY: undefined,
      cropWidth: undefined,
      cropHeight: undefined,
      matType: undefined,
      matOpeningShape: undefined,
      frameMatPricingGuid: undefined,
      finishingCategoryId: undefined,
      finishingServiceId: undefined,
      finishingDetailId: undefined,
      requiredInputs: ['Paper Type', 'Print Size'],
    },
    paperType: {
      label: 'Paper type',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: false,
      disabledMessage: '',
      style: {
        gridColumn: '1',
      },
    },
    printSize: {
      label: 'Print size',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a paper type',
      style: {
        gridColumn: '1',
      },
    },
    finishingService: {
      label: 'Finishing service',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a paper type and print size',
      style: {
        gridColumn: 'span 2',
      },
    },
    matFormat: {
      label: 'Mat Type',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a finishing service',
      style: {
        gridColumn: 'span 2',
      },
    },
    matSize: {
      label: 'Mat size',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a mat format',
      style: {
        gridColumn: 'span 2',
      },
    },

    matOpeningShape:{
      label: 'Mat opening shape',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a mat size',
      style: {
        gridColumn: 'span 2',
      },
    },

    mountSize: {
      label: 'Mount size',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a finishing service',
      style: {
        gridColumn: 'span 2',
      },
    },
    mountColor: {
      label: 'Mount Board Color',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: '',
      style: {
        gridColumn: '1',
      },
    },
    frameType: {
      label: 'Frame Name',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a finishing service',
      style: {
        gridColumn: 'span 4',
      },
    },
    frame: {
      label: 'Frame Wood',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a frame name',
      style: {
        gridColumn: 'span 2',
      },
    },
    frameMat: {
      label: 'Frame mat',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a frame name',
      style: {
        gridColumn: 'span 2',
      },
    },
    acrylic: {
      label: 'Acrylic',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a frame name',
      style: {
        gridColumn: 'span 2',
      },
    },
    frameStain: {
      label: 'Frame stain',
      options: [],
      selectedValue: undefined,
      selectedValuePrice: 0,
      isLoading: false,
      isDisabled: true,
      disabledMessage: 'Please select a frame name',
      style: {
        gridColumn: 'span 2',
      },
    },
  },
  form: {
    meta: {
      subtotal: 0,
      xAspectRatio: undefined,
      yAspectRatio: undefined,
      aspectRatio: undefined,
      cropPositionX: undefined,
      cropPositionY: undefined,
      cropWidth: undefined,
      cropHeight: undefined,
      matType: undefined,
      matOpeningShape: undefined,
      frameMatPricingGuid: undefined,
      finishingCategoryId: undefined,
      finishingServiceId: undefined,
      finishingDetailId: undefined,
      requiredInputs: ['Paper Type', 'Print Size'],
    },
    inputs: {
      paperType: {
        label: 'Paper type',
        options: [],
        selectedValue: undefined,
        selectedValuePrice: 0,
        isLoading: true,
        isDisabled: false,
        disabledMessage: '',
        style: {
          gridColumn: '1 / -1',
        },
      },
      printSize: {
        label: 'Print size',
        options: [],
        selectedValue: undefined,
        selectedValuePrice: 0,
        isLoading: false,
        isDisabled: true,
        disabledMessage: 'Please select a paper type',
        style: {
          gridColumn: '1',
        },
      },
      finishingService: {
        label: 'Finishing service',
        options: [],
        selectedValue: undefined,
        selectedValuePrice: 0,
        isLoading: false,
        isDisabled: true,
        disabledMessage: 'Please select a paper type and print size',
        style: {
          gridColumn: 'span 2',
        },
      },
    },
  },
  setForm: (formOrUpdater) => {
    if (typeof formOrUpdater === 'function') {
      set((state) => ({ form: formOrUpdater(state.form) }));
    } else {
      set({ form: formOrUpdater });
    }
  },

  // Helper to get default form state
  getInitialForm: () => {
    const { defaultOptions } = get();
    return {
      meta: defaultOptions.meta,
      inputs: {
        paperType: defaultOptions.paperType,
        printSize: defaultOptions.printSize,
        finishingService: defaultOptions.finishingService,
      },
    };
  },

  // Helper to get base meta object with preserved values
  getBaseMeta: (prevState) => ({
    subtotal: prevState.meta.subtotal,
    xAspectRatio: prevState.meta.xAspectRatio,
    yAspectRatio: prevState.meta.yAspectRatio,
    aspectRatio: prevState.meta.aspectRatio,
    cropPositionX: prevState.meta.cropPositionX,
    cropPositionY: prevState.meta.cropPositionY,
    cropWidth: prevState.meta.cropWidth,
    cropHeight: prevState.meta.cropHeight,
    matType: prevState.meta.matType,
    matOpeningShape: prevState.meta.matOpeningShape,
    frameMatPricingGuid: prevState.meta.frameMatPricingGuid,
    finishingCategoryId: prevState.meta.finishingCategoryId,
    finishingServiceId: prevState.meta.finishingServiceId,
    finishingDetailId: prevState.meta.finishingDetailId,
  }),

  getCoreInputs: () => {
    const { defaultOptions } = get();
    return Object.keys({
      paperType: defaultOptions.paperType,
      printSize: defaultOptions.printSize,
      finishingService: defaultOptions.finishingService,
    });
  },

  getInputMetaEffects: (inputKey, currentForm) => {
    if (!currentForm || !currentForm.inputs || !currentForm.inputs[inputKey]) {
      return {
        affectsCategoryId: false,
        affectsServiceId: false,
        affectsDetailId: false,
      };
    }

    const inputKeys = Object.keys(currentForm.inputs);
    const changedIndex = inputKeys.indexOf(inputKey);
    if (changedIndex === -1) {
      return {
        affectsCategoryId: false,
        affectsServiceId: false,
        affectsDetailId: false,
      };
    }

    const coreInputs = get().getCoreInputs();
    if (coreInputs.includes(inputKey)) {
      return {
        affectsCategoryId: true,
        affectsServiceId: true,
        affectsDetailId: true,
      };
    }

    const inputsAfter = inputKeys.slice(changedIndex + 1);
    const meta = currentForm.meta || {};

    const hasCategoryInputsAfter = inputsAfter.some(key => {
      const input = currentForm.inputs[key];
      return input?.selectedValue &&
        (key === 'matFormat' || key === 'frameType' ||
          (key === 'finishingService' && input.selectedValueText === 'Mounting'));
    });

    // Check if any input after this one could set finishingServiceId
    const hasServiceInputsAfter = inputsAfter.some(key => {
      const input = currentForm.inputs[key];
      return input?.selectedValue &&
        (key === 'frame' || key === 'acrylic' || key === 'matSize' || key === 'mountSize');
    });

    // Check if any input after this one could set finishingDetailId
    const hasDetailInputsAfter = inputsAfter.some(key => {
      const input = currentForm.inputs[key];
      return input?.selectedValue &&
        (key === 'frameStain' || key === 'mountColor');
    });

    // If this input itself sets a meta field, determine which one
    const input = currentForm.inputs[inputKey];

    if (inputKey === 'matFormat' || inputKey === 'frameType' ||
      (inputKey === 'finishingService' && input?.selectedValueText === 'Mounting')) {
      return {
        affectsCategoryId: true,
        affectsServiceId: true,
        affectsDetailId: true,
      };
    }

    if (inputKey === 'frame' || inputKey === 'acrylic' || inputKey === 'matSize' || inputKey === 'mountSize') {
      return {
        affectsCategoryId: false,
        affectsServiceId: true,
        affectsDetailId: true,
      };
    }

    if (inputKey === 'frameStain' || inputKey === 'mountColor') {
      return {
        affectsCategoryId: false,
        affectsServiceId: false,
        affectsDetailId: true,
      };
    }

    return {
      affectsCategoryId: hasCategoryInputsAfter,
      affectsServiceId: hasServiceInputsAfter || hasCategoryInputsAfter,
      affectsDetailId: hasDetailInputsAfter || hasServiceInputsAfter || hasCategoryInputsAfter,
    };
  },

  resetFormInputs: (prevInputs, changedKey = null) => {
    const coreInputs = get().getCoreInputs();
    const inputKeys = Object.keys(prevInputs);

    // If no changedKey, reset all inputs (product type change)
    if (!changedKey) {
      const newInputs = {};
      inputKeys.forEach((key) => {
        if (coreInputs.includes(key)) {
          newInputs[key] = {
            ...prevInputs[key],
            selectedValue: 'default',
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            options: prevInputs[key]?.options || [],
            isLoading: key === 'paperType' ? true : false,
          };
        }
      });
      return newInputs;
    }

    // Reset inputs after the changed key
    const changedIndex = inputKeys.indexOf(changedKey);
    if (changedIndex === -1) return prevInputs;

    const newInputs = {};
    inputKeys.forEach((key, index) => {
      if (index <= changedIndex) {
        newInputs[key] = prevInputs[key];
      } else if (coreInputs.includes(key)) {
        newInputs[key] = {
          ...prevInputs[key],
          selectedValue: 'default',
          selectedValueText: undefined,
          selectedValuePrice: undefined,
          options: prevInputs[key]?.options || [],
          isLoading: false,
        };
      }

    });

    return newInputs;
  },

  // Get meta resets based on input effects
  getMetaResets: (changedKey, currentForm) => {
    const effects = get().getInputMetaEffects(changedKey, currentForm);
    return {
      finishingCategoryId: effects.affectsCategoryId,
      finishingServiceId: effects.affectsServiceId,
      finishingDetailId: effects.affectsDetailId,
    };
  },

  // Reset selected options
  resetSelectedOptions: () => {
    const { defaultOptions, form } = get();
    set({
      subtotal: 0,
      form: {
        meta: defaultOptions.meta,
        inputs: {
          paperType: {
            ...defaultOptions.paperType,
            options: form?.inputs?.paperType?.options || [],
            isLoading: false,
            isDisabled: false,
          },
          printSize: defaultOptions.printSize,
          finishingService: defaultOptions.finishingService,
        },
      },
    });
  },

  // Reset notes completely
  resetNotes: () => {
    set({ notes: { _all: '' } });
  },

// Reset all edit mode state
resetEditMode: () => {
  const { defaultOptions, form } = get();
  useAlbumSelectionStore.setState({
    albumEditLineItem: null,
    isAlbumEditInitializing: false,
  });
  useMultiOpeningMatStore.getState().resetForm();
  set({
    lineItem: '',
    photographGuid: '',
    subtotal: 0,
    productType: 'default',
    isEditInitializing: false,
    notes: { _all: '' },
    form: {
      meta: defaultOptions.meta,
      inputs: {
        paperType: {
          ...defaultOptions.paperType,
          options: form?.inputs?.paperType?.options || [],
          isLoading: false,
          isDisabled: false,
        },
        printSize: defaultOptions.printSize,
        finishingService: defaultOptions.finishingService,
      },
    },
  });
},

  // Get notes value for selected photo
  getNotesValue: () => {
    const { selectedPhoto, notes, productType } = get();
    const photoGuid = productType === 'multi-opening' ? undefined : selectedPhoto?.attributes?.guid;
    return photoGuid ? (notes[photoGuid] || '') : notes._all;
  },

  // Handle selected photo change
  handleSelectedPhoto: (photo) => {
    const { selectedPhoto, productType } = get();
    set({ selectedPhoto: photo, selectAllPhotos: false });
    if (photo !== selectedPhoto && !['artist-proofs', 'test-prints'].includes(productType)) {
      get().resetSelectedOptions();
    }
  },


  handleUploadPhoto: async (files, orderId, queryClient) => {
    const { setApiError, setPhotosLoadingMessage, productType, addPreview, setUploadsPreview } = get();
    if (!orderId) {
      alert('Please create an order before uploading images.');
      return;
    }

  const labOrderGuid = orderId;
  const isAlbum = productType === 'albums';

  if (!isAlbum) {
    setPhotosLoadingMessage('Uploading photos');
  }

  try {
    setApiError(null);
    await Promise.all(
      files.map(async (file) => {
        const photoFileName = sanitize(file.name);
        const tempId = `${Date.now()}-${Math.random()}`;

        addPreview({
          id: tempId,
          fileName: photoFileName,
          uploading: true,
          productType,
        });

          const { signedUrl } = await httpClient.post(
            `lab_orders/${labOrderGuid}/create_presigned_url`,
            { labOrderGuid, photoFileName, productType }
          );

          await axios.put(signedUrl, file, {
            headers: { 'Content-Type': file.type },
          });

          await httpClient.patch(
            `lab_orders/${labOrderGuid}/photograph_uploaded`,
            { labOrderGuid, photoFileName }
          );
      })
    );

    await queryClient.invalidateQueries({
      queryKey: ['photos', orderId],
    });
    setUploadsPreview([]);
  } catch (err) {
    setApiError(apiErrorMessage(err));
  }
},
  
  handleAddToCart: async (orderId, lineItemId, quantityValue, navigate, queryClient) => {
    const {
      form,
      selectedPhoto,
      selectAllPhotos,
      productType,
      photos,
      getNotesValue,
      setModalIsOpen,
      setAddToCart,
      setForm,
      setApiError,
      addLineItem,
      addAlbumLineItem,
      addMultiOpeningLineItem,
      updateMultiOpeningLineItem,
      setSubtotal,
      updateLineItem,
      fetchLabOrder,
      resetNotes,
      resetSelectedOptions,
      setProductType,
      setPhotos,
      setIsSuccessModalOpen,
      setSuccessModalMessage,
    } = get();

    if (productType === 'multi-opening') {
      const moStore = useMultiOpeningMatStore.getState();
      const assignedPhotoGuids = new Set(
        Object.values(moStore.openingAssignments)
          .filter(Boolean)
          .map((photo) => photo.attributes?.guid),
      );
      const markAssignedPhotosInCart = () => {
        const markPhoto = (photo) => assignedPhotoGuids.has(photo.attributes?.guid)
          ? { ...photo, attributes: { ...photo.attributes, inCart: true, isAddedToCart: true } }
          : photo;
        setPhotos(photos.map(markPhoto));
        queryClient?.setQueryData(['photos', orderId], (cached) => cached && ({
          ...cached,
          data: cached.data.map(markPhoto),
        }));
      };

      if (!moStore.isFormValid()) {
        const missingOpenings = moStore.getMissingOpenings();
        const requiredInputs = moStore.form.meta.requiredInputs || [];
        const messages = [
          ...requiredInputs,
          ...(missingOpenings > 0 ? [`Add ${missingOpenings} opening(s) to the selected Mat/Frame`] : []),
        ];
        setForm((prev) => ({
          ...prev,
          meta: {
            ...prev.meta,
            reviewOrderError: `To add this item to your cart, please complete: ${messages.join(', ')}.`,
          },
        }));
        setModalIsOpen(true);
        return;
      }

      setAddToCart(true);
      const notesValue = getNotesValue();
      const body = moStore.buildAddToCartBody(quantityValue, notesValue);

      if (lineItemId) {
        try {
          await updateMultiOpeningLineItem(orderId, lineItemId, body);
          await fetchLabOrder(orderId);
          if (queryClient) {
            await queryClient.invalidateQueries({ queryKey: ['photos', orderId] });
          }
          markAssignedPhotosInCart();
          setSuccessModalMessage('Successfully Updated.');
          setIsSuccessModalOpen(true);
        } catch (err) {
          setApiError(apiErrorMessage(err));
        } finally {
          setAddToCart(false);
        }
        return;
      }

      try {
        const data = await addMultiOpeningLineItem(orderId, body);
        if (data.data) {
          await fetchLabOrder(orderId);
          if (queryClient) {
            await queryClient.invalidateQueries({ queryKey: ['photos', orderId] });
          }
          markAssignedPhotosInCart();
          moStore.resetForm();
          resetNotes();
          setSubtotal(0);
          setSuccessModalMessage('Successfully Added to Cart.');
          setIsSuccessModalOpen(true);
        }
      } catch (err) {
        setApiError(apiErrorMessage(err));
      } finally {
        setAddToCart(false);
      }
      return;
    }

    if (productType === 'albums') {
      const {
        albumForm,
        isAlbumFormValid,
        getAlbumMissingRequiredSelectionLabels,
        albumItems,
        resetAlbumForm,
        setAlbumForm,
        updateAlbumLineItem,
      } = useAlbumSelectionStore.getState();
      const albumTypeVal = albumForm?.inputs?.albumType?.selectedValue;
      if (COMING_SOON_ALBUM_TYPES.includes(albumTypeVal)) {
        return;
      }

      if (!isAlbumFormValid()) {
        const missing = getAlbumMissingRequiredSelectionLabels();
        const list =
          missing.length > 0
            ? missing.join(', ')
            : (albumForm.meta.requiredInputs || []).join(', ');
        setAlbumForm((prevState) => ({
          ...prevState,
          meta: {
            ...prevState.meta,
            reviewOrderError: `To add this item to your cart, please select ${list}.`,
          },
        }));
        setModalIsOpen(true);
        return;
      }

      const spreadError = getAlbumSpreadError(albumItems, albumForm);
      
      if (spreadError) {
        setAlbumForm((prevState) => ({
          ...prevState,
          meta: { ...prevState.meta, reviewOrderError: spreadError },
        }));
        setModalIsOpen(true);
        return;
      }

      setAddToCart(true);

      const orderedPhotos = albumItems?.length > 0 ? albumItems : photos;

      if (!orderedPhotos?.length) {
        setAddToCart(false);
        setAlbumForm((prevState) => ({
          ...prevState,
          meta: {
            ...prevState.meta,
            reviewOrderError: 'Please upload at least one photo for your album.',
          },
        }));
        setModalIsOpen(true);
        return;
      }

      const notesValue = getNotesValue();
      const body = buildAlbumLineItemRequestBody(
        albumForm,
        orderedPhotos,
        notesValue,
        Boolean(lineItemId),
      );

      if (body.error) {
        setAddToCart(false);
        setAlbumForm((prevState) => ({
          ...prevState,
          meta: {
            ...prevState.meta,
            reviewOrderError: body.error,
          },
        }));
        setModalIsOpen(true);
        return;
      }

      if (lineItemId) {
        try {
          await updateAlbumLineItem(orderId, lineItemId, body);
          await fetchLabOrder(orderId);
          if (queryClient) {
            await queryClient.invalidateQueries({ queryKey: ['labOrder', orderId, lineItemId] });
          }
          setSuccessModalMessage('Successfully Updated.');
          setIsSuccessModalOpen(true);
        } catch (err) {
          console.log('lineError', err);
          setApiError(apiErrorMessage(err));
        } finally {
          setAddToCart(false);
        }
        return;
      }

      try {
        const data = await addAlbumLineItem(orderId, body);
        if (data.data) {
          await fetchLabOrder(orderId);
          if (queryClient) {
            await queryClient.invalidateQueries({ queryKey: ['photos', orderId] });
            // await queryClient.invalidateQueries({ queryKey: ['labOrder', orderId] });
          }
          get().setUploadsPreview([]);
          resetNotes();
          setSubtotal(0);
          resetSelectedOptions();
          resetAlbumForm();
          setSuccessModalMessage('Successfully Added to Cart.');
          setIsSuccessModalOpen(true);
        }
      } catch (err) {
        console.log('lineError', err);
        setApiError(apiErrorMessage(err));
      } finally {
        setAddToCart(false);
      }
      return;
    }

    if (selectedPhoto === '') {
      setForm((prevState) => ({
        ...prevState,
        meta: {
          ...prevState.meta,
          reviewOrderError: 'Please upload at least one photo before adding to cart.',
        },
      }));
      setModalIsOpen(true);
      return;
    }

    const shouldValidateForm =
      productType !== 'canvas' ||
      isFramedCanvasPaper(form?.inputs?.paperType?.selectedValueText);

    if (
      productType !== 'test-prints' &&
      shouldValidateForm &&
      form?.meta?.requiredInputs?.length > 0
    ) {
      setModalIsOpen(true);
      return;
    }
    setAddToCart(true);
    const notesValue = getNotesValue();

    const selectedPhotos = selectAllPhotos ? photos : [selectedPhoto];
    const body = {
      line_items: selectedPhotos.map((photo) => ({
          imageName: photo.attributes.name,
          quantity: parseInt(quantityValue, 10),
          print_size: form.inputs.printSize.selectedValue,
          ...(form.inputs.editingSoftware?.selectedValue &&
            form.inputs.editingSoftware.selectedValue !== 'default' && {
              editing_software_guid: form.inputs.editingSoftware.selectedValue,
            }),
          finishing_service: form.meta.finishingServiceId,
          finishing_service_detail: form.meta.finishingDetailId,
          frame_mat: form.meta.frameMatPricingGuid,
          print_border: 'None',
          notes: notesValue,
          source_photograph_guid: photo.attributes.guid,
          photograph: {
            source_photograph_guid: photo.attributes.guid,
            ...(!selectAllPhotos && {
              crop_x: form.meta.cropPositionX,
              crop_y: form.meta.cropPositionY,
              crop_h: form.meta.cropHeight,
              crop_w: form.meta.cropWidth,
            }),
            photo_meta: {
              ...(!selectAllPhotos && {
                x: form.meta.cropPositionX,
                y: form.meta.cropPositionY,
                width: form.meta.cropWidth,
                height: form.meta.cropHeight,
              }),
              selectedPaper: form.inputs.paperType?.selectedValueText,
              unit: 'px',
              aspect: form.meta.aspectRatio,
              x_aspect: form.meta.xAspectRatio,
              y_aspect: form.meta.yAspectRatio,
              locked: false,
              keepSelection: true,
              border: 'none',
              imageGuid: photo.attributes.guid,
              imageRef: photo.attributes.guid,
              ref: photo.attributes.guid,
            },
          },
      })),
    };

    if (lineItemId) {
      try {
        await updateLineItem(orderId, lineItemId, body);
        await fetchLabOrder(orderId);
        setSuccessModalMessage('Successfully Updated.');
        setIsSuccessModalOpen(true);
      } catch (err) {
        console.log('lineError', err);
        setApiError(apiErrorMessage(err));
      } finally {
        setAddToCart(false);
      }
    } else {
      try {
        const data = await addLineItem(orderId, body);

        if (data.data) {
          await fetchLabOrder(orderId);
          get().setUploadsPreview([]);
          resetNotes();
          setSubtotal(0);
          resetSelectedOptions();
          set({ selectAllPhotos: false });
          setSuccessModalMessage('Successfully Added to Cart.');
          setIsSuccessModalOpen(true);
        }
      } catch (err) {
        console.log('lineError', err);
        setApiError(apiErrorMessage(err));
      } finally {
        setAddToCart(false);
      }
    }
  },
  handleResetPhotos: async (orderId, productType, queryClient) => {
    const { photos } = get();
    set({ isResetting: true, photosLoadingMessage: 'Loading photographs...', apiError: null });
    try {
      const order = await queryClient.fetchQuery({
        queryKey: ['labOrder', orderId, undefined],
        queryFn: () => httpClient.get(`lab_orders/${orderId}/retrieve_full_order`),
        staleTime: 0,
      });
      const imagesInCart = new Set(order.data.attributes.imagesInCart || []);
      const resettablePhotos = photos.filter(
        (photo) => !imagesInCart.has(photo.attributes?.guid),
      );
      const ids = resettablePhotos
        .map((photo) => photo.attributes?.guid || photo.id)
        .filter(Boolean);
      if (!ids.length) return false;

      const remainingPhotos = photos.filter((photo) => !resettablePhotos.includes(photo));

      await httpClient.delete(
        productType === 'multi-opening'
          ? `lab_orders/${orderId}/multi_openings_line_items/photographs`
          : `lab_orders/${orderId}/photographs`,
        { data: { ids } },
      );
      queryClient.setQueryData(['photos', orderId], (cached) => cached && ({
        ...cached,
        data: cached.data.filter(
          (photo) => !ids.includes(photo.attributes?.guid || photo.id),
        ),
      }));
      set({
        photos: sortPhotosByName(remainingPhotos),
        selectedPhoto: remainingPhotos[0] || '',
        selectAllPhotos: false,
      });
      if (productType === 'multi-opening') {
        useMultiOpeningMatStore.getState().resetOpeningAssignments();
      }
      return true;
    } catch (error) {
      set({ apiError: apiErrorMessage(error) });
      return false;
    } finally {
      set({ isResetting: false, photosLoadingMessage: '' });
    }
  },

  // Handle photo order change
  handleChangePhotoOrder: (images) => {
    set({ photos: images });
  },

  // Fetch lab order and process line item if editing
  fetchLabOrder: async (labOrderId, lineItemId = null) => {
    const { setQueryState, setCartCount, setImageCartCount, setPhotographGuid, setSubtotal, setLineItem, updatePhotosWithCartIcons } = get();
    setQueryState('labOrder', { isLoading: true, isError: false, isSuccess: false });

    try {
      const response = await httpClient.get(`lab_orders/${labOrderId}/retrieve_full_order`);

      const { imagesInCart } = response.data.attributes;
      setCartCount(response.included.length)
      setImageCartCount(imagesInCart.length);

      // Update photos with cart icons
      updatePhotosWithCartIcons(imagesInCart);

      // If editing a line item, find and set it
      if (lineItemId) {
        const matchingOption = response?.included?.find(
          obj => obj.attributes.guid === lineItemId
        );
        if (matchingOption) {
          setPhotographGuid(matchingOption.attributes?.photographGuid);
          const rawSubtotal = parseFloat(matchingOption.attributes?.subtotal);
          setSubtotal(isNaN(rawSubtotal) ? '0.00' : rawSubtotal.toFixed(2));
          setLineItem(matchingOption);
        }
      }

      setQueryState('labOrder', { isLoading: false, isError: false, isSuccess: true });
      return response;
    } catch (err) {
      setQueryState('labOrder', { isLoading: false, isError: true, isSuccess: false });
      throw err;
    }
  },

  // Process paper type data
  processPaperTypeData: (response) => {
    const { defaultOptions, setForm, lineItem, setProductType, isEditInitializing, productType } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    const paperTypeOptions = response.data.map((paperType) => {
      const originalName = normalizePaperName(paperType.attributes.name);
      return {
        value: paperType.id,
        guid: paperType.attributes.guid,
        text: originalName,
      };
    });

    const sortedPaperList = paperTypeOptions.filter(paper => !paper?.text?.toLowerCase().includes('no paper'));
    sortedPaperList.sort((a, b) => {
      const indexA = paperListOrder.indexOf(a.text);
      const indexB = paperListOrder.indexOf(b.text);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.text.localeCompare(b.text);
    });

    // For test-prints, store options without disrupting the test-prints form state
    if (productType === 'test-prints') {
      setForm((prevState) => ({
        ...prevState,
        inputs: {
          ...prevState.inputs,
          paperType: {
            ...prevState.inputs?.paperType,
            options: sortedPaperList,
            isLoading: false,
          },
        },
      }));
      return;
    }

    // Only match paper if we have a lineItem AND we are in edit initialization mode
    const savedPaperName = normalizePaperName(
      lineItem?.attributes?.printSizeAttributes?.paper || lineItem?.attributes?.paperName,
    );
    const savedPaperGuid =
      lineItem?.attributes?.printSizeAttributes?.paperGuid ||
      lineItem?.attributes?.paperGuid;
    const matchedPaper = shouldAutoMap
      ? sortedPaperList.find(
          (paper) =>
            paper.value === savedPaperGuid ||
            paper.guid === savedPaperGuid ||
            paper.text === savedPaperName ||
            (savedPaperName === FRAMED_CANVAS_DISPLAY_NAME &&
              paper.text === FRAMED_CANVAS_API_NAME),
        )
      : null;

    // Set product type based on paper only during initialization
    if (shouldAutoMap) {
    const currentProductType = get().productType;
    if (!currentProductType || currentProductType === 'default') {
      const paperTypeText = matchedPaper?.text?.toLowerCase() ||
        normalizePaperName(lineItem?.attributes?.printSizeAttributes?.paper)?.toLowerCase() || '';
      if (paperTypeText.includes('canvas')) {
        setProductType('canvas');
      } else if (paperTypeText) {
        setProductType('prints');
      }
    }
  }

    setForm((prevState) => ({
      meta: defaultOptions.meta,
      inputs: {
        paperType: {
          ...prevState.inputs.paperType,
          options: sortedPaperList,
          isLoading: false,
          selectedValue: matchedPaper?.value || 'default',
          selectedValueText: matchedPaper?.text || '',
        },
        printSize: defaultOptions.printSize,
        finishingService: defaultOptions.finishingService,
        ...(productType === 'artist-proofs' && {
          editingSoftware: {
            label: 'Editing Software',
            options: [],
            selectedValue: 'default',
            selectedValueText: undefined,
            isLoading: false,
            isDisabled: true,
          },
        }),
      },
    }));
  },

  // Reset print size form to default
  resetPrintSizeForm: () => {
    const { form, defaultOptions, setForm } = get();
    setForm({
      meta: defaultOptions.meta,
      inputs: {
        paperType: {
          ...defaultOptions.paperType,
          options: form.inputs.paperType.options,
          isLoading: false,
          isDisabled: false,
        },
        printSize: defaultOptions.printSize,
        finishingService: defaultOptions.finishingService,
      },
    });
  },

  // Set print size loading state
  setPrintSizeLoading: (isLoading) => {
    const { setForm } = get();
    setForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        printSize: { ...prevState.inputs.printSize, isLoading },
      },
    }));
  },

  // Process print size data
  processPrintSizeData: (response) => {
    const { defaultOptions, setForm, lineItem, isEditInitializing, productType } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    const productPrintSizes = ['artist-proofs', 'test-prints'].includes(productType)
      ? response
      : filterOutSpecialProductPrintSizes(response);
    const sortedPrintSizes = filterOutMultiOpeningPrintSizes(productPrintSizes).sort((a, b) => {
      if (Number(a.xAspect) < Number(b.xAspect)) return -1;
      if (Number(a.xAspect) > Number(b.xAspect)) return 1;
      if (Number(a.yAspect) < Number(b.yAspect)) return -1;
      if (Number(a.yAspect) > Number(b.yAspect)) return 1;
      return 0;
    });

    const sortedPrintSizeOptions = sortedPrintSizes.map((printSize) => ({
      value: printSize.guid,
      text: `${printSize.name}`,
      price: printSize.price,
      xAspectRatio: printSize.xAspect,
      yAspectRatio: printSize.yAspect,
    }));

    const savedPrintSizeGuid = lineItem?.attributes?.printSizeAttributes?.printSizeGuid;
    const savedPrintSizeName =
      lineItem?.attributes?.printSizeAttributes?.printSize || lineItem?.attributes?.printSizeName;
    const matchedPrintSizeOption = shouldAutoMap ? sortedPrintSizeOptions.find(option =>
      option.value === savedPrintSizeGuid || option.text.includes(savedPrintSizeName)
    ) : null;

    setForm((prevState) => ({
      meta: {
        ...defaultOptions.meta,
        paperTypeId: prevState.meta.paperTypeId,
        aspectRatio: matchedPrintSizeOption?.xAspectRatio / matchedPrintSizeOption?.yAspectRatio,
        xAspectRatio: matchedPrintSizeOption?.xAspectRatio,
        yAspectRatio: matchedPrintSizeOption?.yAspectRatio,
      },
      inputs: {
        paperType: prevState.inputs.paperType,
        printSize: {
          ...defaultOptions.printSize,
          options: sortedPrintSizeOptions,
          isLoading: false,
          isDisabled: false,
          selectedValue: matchedPrintSizeOption?.value,
          selectedValueText: matchedPrintSizeOption?.text,
          selectedValuePrice: matchedPrintSizeOption?.price
        },
        finishingService: defaultOptions.finishingService,
        ...(productType === 'artist-proofs' && {
          editingSoftware: prevState.inputs.editingSoftware,
        }),
      },
    }));
  },

  // Process editing software data
  processEditingSoftwareData: (response) => {
    const { setForm, lineItem, isEditInitializing } = get();
    const shouldAutoMap = lineItem && isEditInitializing;
    const responseData = response.data;
    const editingSoftwareOptions = responseData
      .filter((option) => option.attributes.active)
      .map((option) => ({
        value: option.attributes.guid,
        text: option.attributes.name,
      }));
    const savedSoftwareGuid = lineItem?.attributes?.editingSoftwareGuid;
    const savedSoftwareName = lineItem?.attributes?.editingSoftware;
    const matchedSoftware = shouldAutoMap
      ? editingSoftwareOptions.find(
          (option) =>
            option.value === savedSoftwareGuid || option.text === savedSoftwareName,
        )
      : null;

    setForm((prevState) => {
      const currentSoftware = prevState.inputs.editingSoftware;

      return {
        ...prevState,
        inputs: {
          ...prevState.inputs,
          editingSoftware: {
            ...currentSoftware,
            label: 'Editing Software',
            options: editingSoftwareOptions,
            selectedValue: matchedSoftware?.value || 'default',
            selectedValueText: matchedSoftware?.text,
            isLoading: false,
            isDisabled: false,
          },
        },
      };
    });
  },

  // Set finishing service loading state
  setFinishingServiceLoading: (isLoading) => {
    const { setForm } = get();
    setForm((prevState) => ({
      ...prevState,
      inputs: {
        ...prevState.inputs,
        finishingService: { ...prevState.inputs.finishingService, isLoading },
      },
    }));
  },

  // Set frame inputs loading state (for framing)
  setFrameInputsLoading: (isLoading) => {
    const { setForm, defaultOptions, form } = get();
    
    // Only add frame inputs if we're in framing mode and frameType is selected
    if (!form.inputs?.frameType?.selectedValue || form.inputs.frameType.selectedValue === 'default') return;
    
    const frameTypeText = form.inputs.frameType.selectedValueText;
    
    setForm((prevState) => ({
      meta: {
        ...prevState.meta,
        requiredInputs: ['Frame', 'Frame Mat', 'Acrylic',
          ...((frameTypeText === 'Hardwood Frame' || frameTypeText === 'Hardwood Float Canvas Frame') 
            ? ['Frame Stain'] : [])
        ],
      },
      inputs: {
        paperType: prevState.inputs.paperType,
        printSize: prevState.inputs.printSize,
        finishingService: prevState.inputs.finishingService,
        frameType: prevState.inputs.frameType,
        frame: { 
          ...defaultOptions.frame, 
          isLoading,
          isDisabled: false,
        },
        frameMat: {
          ...defaultOptions.frameMat,
          isLoading,
          isDisabled: true,
        },
        acrylic: {
          ...defaultOptions.acrylic,
          isLoading,
          isDisabled: true,
        },
        ...((frameTypeText === 'Hardwood Frame' || frameTypeText === 'Hardwood Float Canvas Frame') && {
          frameStain: {
            ...defaultOptions.frameStain,
            isLoading,
            isDisabled: true,
          },
        }),
      },
    }));
  },

  // Process finishing categories data
  processFinishingCategoriesData: (response) => {
    const { setForm, lineItem, setFinishingCategoriesData, isEditInitializing } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    setFinishingCategoriesData(response);

    const finishingCategories = response.data.map((category) => ({
      value: category.attributes.guid,
      text: category.attributes.finishingCategoryPrimaryName,
    }));

    if (finishingCategories.length === 0) {
      finishingCategories.push({ value: 'None', text: 'None' });
    }

    const uniqueFinishingCategories = Array.from(
      finishingCategories.reduce((map, obj) => map.set(obj.text, obj), new Map()).values(),
    );

    let matchedFinishingCategory = null;
    if (shouldAutoMap) {
      let updateFinishingCategory;
      if (lineItem?.attributes?.finishingService?.finishingCategory?.toLowerCase().includes('fram')) {
        updateFinishingCategory = 'framing';
      } else if (lineItem?.attributes?.finishingService?.finishingCategory?.toLowerCase().includes('mount')) {
        updateFinishingCategory = 'mount';
      } else if (lineItem?.attributes?.finishingService?.finishingCategory?.toLowerCase().includes('mat')) {
        updateFinishingCategory = 'mat';
      }

      matchedFinishingCategory = uniqueFinishingCategories.find(opt =>
        opt.text?.toLowerCase().includes(updateFinishingCategory)
      );
    }

    setForm((prevState) => {
      const newState = { ...prevState };
      newState.inputs.finishingService.options = uniqueFinishingCategories;
      newState.inputs.finishingService.selectedValue = matchedFinishingCategory?.value;
      newState.inputs.finishingService.selectedValueText = matchedFinishingCategory?.text;
      newState.inputs.finishingService.isLoading = false;
      newState.inputs.finishingService.isDisabled = false;
      return newState;
    });
  },

  // Process finishing services data
  processFinishingServicesData: (response) => {
    const { form, defaultOptions, setForm, lineItem, setFinishingServicesData, isEditInitializing } = get();
    const finishingServiceText = form.inputs.finishingService?.selectedValueText;
    const shouldAutoMap = lineItem && isEditInitializing;

    setFinishingServicesData(response);

    if (finishingServiceText === 'Framing') {
      // Process framing data
      let frames;
      const frameTypeText = form.inputs.frameType?.selectedValueText;

      // Circle/Oval frame types collapse the "Frame Name" step: each finishing
      // category is already a fully-named frame (size + finish + shape), so the
      // cascade jumps straight to Frame Mat then Acrylic.
      if (isCircleOrOvalFrameType(frameTypeText)) {
        // Frame Mat here is really "mat type" (8-ply vs double 4-ply) - derive
        // the products/frame_mats mat_type key from each service's name so the
        // options and the eventual selection line up with real backend values
        // instead of the raw compound name text.
        const availableMatTypeKeys = Array.from(
          new Set(
            response.data
              .map((service) =>
                deriveCircleOvalMatTypeKey(getCircleOvalServiceMatNameSuffix(service)),
              )
              .filter(Boolean),
          ),
        ).sort();
        const frameMatOptions = availableMatTypeKeys.map((key) => ({
          value: key,
          text: MAT_TYPE_LABELS[key] || key,
        }));
        if (frameMatOptions.length === 0) {
          frameMatOptions.push({ value: 'None', text: 'None' });
        }

        let matchedFrameMat;
        if (shouldAutoMap) {
          // Guid match only works if the line item's original price list is
          // still the active one - price lists get regenerated with fresh
          // guids, so fall back to matching the exact (deterministically
          // generated) service name when the guid is no longer present.
          const serviceGuid = lineItem?.attributes?.finishingService?.finishingServiceGuid;
          const serviceName = lineItem?.attributes?.finishingService?.finishingService;
          const matchedService =
            response.data.find(
              (service) => service.id === serviceGuid || service.attributes.guid === serviceGuid,
            ) || response.data.find((service) => service.attributes.name === serviceName);
          const matTypeKey = deriveCircleOvalMatTypeKey(
            getCircleOvalServiceMatNameSuffix(matchedService),
          );
          if (matTypeKey) {
            matchedFrameMat = frameMatOptions.find((opt) => opt.value === matTypeKey);
          }
        }

        setForm((prevState) => ({
          meta: {
            ...get().getBaseMeta(prevState),
            requiredInputs: ['Frame Mat', 'Acrylic'],
          },
          inputs: {
            paperType: prevState.inputs.paperType,
            printSize: prevState.inputs.printSize,
            finishingService: prevState.inputs.finishingService,
            frameType: prevState.inputs.frameType,
            frameMat: {
              ...defaultOptions.frameMat,
              options: frameMatOptions,
              selectedValue: matchedFrameMat?.value,
              selectedValueText: matchedFrameMat?.text,
              isLoading: false,
              isDisabled: false,
            },
            acrylic: defaultOptions.acrylic,
          },
        }));

        if (shouldAutoMap && matchedFrameMat?.value) {
          setTimeout(() => {
            const { processFrameMatSelection } = get();
            processFrameMatSelection(matchedFrameMat.value, matchedFrameMat.text);
          }, 0);
        }
        return;
      }

      if (frameTypeText === 'Contemporary Frame') {
        frames = response.data.map((service) => ({
          value: service.id,
          name: service.attributes.frameWoodPricing,
          price: parseFloat(service.attributes.price)
        }));
      } else if (frameTypeText === 'Hardwood Frame') {
        frames = response.data.map((service) => ({
          value: service.id,
          name: service.attributes.frameWoodAndProfileName,
          price: parseFloat(service.attributes.price)
        }));
      } else if (frameTypeText === 'Desktop Frame') {
        frames = response.data.map((service) => ({
          value: service.id,
          name: service.attributes.frameWoodPricing,
          price: parseFloat(service.attributes.price)
        }));
      } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
        frames = response.data.map((service) => {
          const desc = service.attributes.description || '';
          const shortName = desc.split(' for a')[0].replace(': ', ' - ');
          return {
            value: service.id,
            name: shortName || desc,
            price: parseFloat(service.attributes.price)
          };
        });
      } 

      const uniqueFrames = Array.from(
        new Map(frames?.map((frame) => [frame.name, frame])).values()
      );
      const sortedUniqueFrames = uniqueFrames.sort((a, b) => a.name.localeCompare(b.name))
      
      // Only auto-map from lineItem during initial edit load
      let matchingFrame;
      if (shouldAutoMap && lineItem?.attributes?.finishingService?.finishingServiceGuid) {
        const serviceGuid = lineItem.attributes.finishingService.finishingServiceGuid;
        const matchedService = response.data.find(
          (service) => service.id === serviceGuid || service.attributes.guid === serviceGuid
        );
        
        if (matchedService) {
          // Extract frame name based on frame type
          let frameName;
          if (frameTypeText === 'Contemporary Frame' || frameTypeText === 'Desktop Frame') {
            frameName = matchedService.attributes.frameWoodPricing;
          } else if (frameTypeText === 'Hardwood Frame') {
            frameName = matchedService.attributes.frameWoodAndProfileName;
          } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
            frameName = getFloatCanvasFrameName(matchedService);
          }
          
          if (frameName) {
            matchingFrame = sortedUniqueFrames.find(
              (frame) => frame.name === frameName
            );
          }
        }
      }
      
      // Fall back to text matching if GUID matching didn't work (only during initial edit)
      if (!matchingFrame && shouldAutoMap) {
        let updateFrame;
        if (frameTypeText === 'Contemporary Frame' || frameTypeText === 'Desktop Frame') {
          updateFrame = lineItem?.attributes?.finishingService?.formattedDetailOne?.toLowerCase();
        } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
          updateFrame = lineItem?.attributes?.finishingService?.formattedDetailOne?.toLowerCase();
        } else if (frameTypeText === 'Hardwood Frame') {
          
          updateFrame = lineItem?.attributes?.finishingService?.formattedFrameProfile?.toLowerCase();
          if (!updateFrame) {
            const detailOne = lineItem?.attributes?.finishingService?.formattedDetailOne || '';
            const match = detailOne.match(/^([^:]+):\s*(0\.5)\s*inch\s*Profile$/i);
            if (match) {
              const wood = match[1].trim();
              const profileSize = match[2].trim();
       
              const formattedProfile = `${wood} - ${profileSize}" Profile`;
              updateFrame = formattedProfile.toLowerCase();
            } else {
              updateFrame = detailOne.toLowerCase();
            }
          }
        } else {
          updateFrame = lineItem?.attributes?.finishingService?.formattedFrameProfile?.toLowerCase();
        }
        
        // Extract the wood type and match it
        if (frameTypeText === 'Hardwood Float Canvas Frame' && updateFrame) {
          const woodTypeMatch = updateFrame.match(/^([^:]+)/);
          if (woodTypeMatch) {
            const woodType = woodTypeMatch[1].trim().toLowerCase();
            matchingFrame = sortedUniqueFrames.find((obj) => 
              obj.name?.toLowerCase().startsWith(woodType)
            );
          }
        } else if (updateFrame) {
          matchingFrame = sortedUniqueFrames.find((obj) => updateFrame?.includes(obj.name?.toLowerCase()));
        }
      }

      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          requiredInputs: ['Frame', 'Frame Mat', 'Acrylic',
            ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
              || prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' ) ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
          frame: {
            ...defaultOptions.frame,
            options: sortedUniqueFrames.map((frame) => ({
              value: frame.value,
              text: frame.name,
              price: frame.price,
            })),
            selectedValue: matchingFrame?.value,
            selectedValueText: matchingFrame?.name,
            selectedValuePrice: matchingFrame?.price,
            isLoading: false,
            isDisabled: false,
          },
          frameMat: defaultOptions.frameMat,
          acrylic: defaultOptions.acrylic,
          ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
            || prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' )  && { frameStain: defaultOptions.frameStain }),
        },
      }));

      // If initial edit and frame is set, trigger processFrameSelection to populate frame mats
      if (shouldAutoMap && matchingFrame?.value) {
        setTimeout(() => {
          const { processFrameSelection } = get();
          processFrameSelection(matchingFrame.value, matchingFrame.name);
        }, 0);
      }
    } else if (finishingServiceText === 'Matting') {
      // Process matting data
      const matFormatText = form.inputs.matFormat.selectedValueText;
      const matSizes = response.data
        .filter((service) =>
          service.attributes.finishingCategory.includes(matFormatText)
        )
        .map((service) => ({
          value: service.id,
          text: `${service.attributes.name} ($${parseFloat(service.attributes.price).toFixed(2)})`,
          price: service.attributes.price,
          name: service.attributes.name,
        }));

      const sortedMatSizes = matSizes.sort((a, b) => {
        if (a.text < b.text) return -1;
        if (a.text > b.text) return 1;
        return 0;
      });
      
      let matchedMatSize;
      if (shouldAutoMap) {
        // Guid match only works if the line item's original price list is
        // still the active one - price lists get regenerated, and
        // finishing_service_pricings are re-created with new guids each
        // time, so an older line item's saved guid will not exist in a
        // freshly-generated price list. Name match survives regeneration
        // since size/price generation is deterministic (e.g. "3 inch -
        // (12x15)" is produced the same way every time).
        const serviceGuid = lineItem?.attributes?.finishingService?.finishingServiceGuid;
        const serviceName = lineItem?.attributes?.finishingService?.finishingService;
        matchedMatSize =
          sortedMatSizes.find((matSize) => matSize.value === serviceGuid) ||
          sortedMatSizes.find((matSize) => matSize.name === serviceName);
      }

      let finalMatSize = matchedMatSize;
  
      if (!matchedMatSize && shouldAutoMap && sortedMatSizes.length === 0) {
        finalMatSize = { value: 'none', text: 'None', price: 0 };
        sortedMatSizes.push(finalMatSize);
      }

      const xRatio = form.meta?.xAspectRatio;
      const yRatio = form.meta?.yAspectRatio;
      const isSquare = xRatio != null && yRatio != null && xRatio === yRatio;

      const paperNameText = (form.inputs.paperType?.selectedValueText || '').toLowerCase();
      const forcedOpeningShape = paperNameText.includes('circle')
        ? 'circle'
        : paperNameText.includes('oval')
          ? 'oval'
          : undefined;
      const forcedOpeningShapeLabel = forcedOpeningShape === 'circle' ? 'Circle' : 'Oval';

      const openingShapeOptions = forcedOpeningShape
        ? [{ value: forcedOpeningShape, text: forcedOpeningShapeLabel }]
        : [
            { value: 'standard', text: 'Standard' },
            ...(isSquare ? [{ value: 'circle', text: 'Circle' }] : [{ value: 'oval', text: 'Oval' }]),
          ];

      const finalMatSizeSelected = !!finalMatSize?.value && finalMatSize.value !== 'default';

      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),

          finishingServiceId: finalMatSize?.value,

          requiredInputs: finalMatSizeSelected && !forcedOpeningShape ? ['Mat Opening Shape'] : ['Mat Size'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          matFormat: prevState.inputs.matFormat,
          matSize: {
            ...defaultOptions.matSize,
            options: sortedMatSizes,
            selectedValue: finalMatSize?.value,
            selectedValueText: finalMatSize?.text,
            selectedValuePrice: finalMatSize?.price,
            isLoading: false,
            isDisabled: false,
          },
          matOpeningShape: {
            ...defaultOptions.matOpeningShape,
            options: openingShapeOptions,
            selectedValue: forcedOpeningShape,
            selectedValueText: forcedOpeningShape ? forcedOpeningShapeLabel : undefined,
            isLoading: false,
            isDisabled: false,
            isHidden: !!forcedOpeningShape,
          },
        },
      }));
    } else if (finishingServiceText === 'Mounting') {
      // Process mounting data
      const mountSizes = response.data
        .filter((service) => service.attributes.finishingCategory === 'Mount')
        .map((service) => ({
          value: service.id,
          text: `${service.attributes.name} ($${parseFloat(service.attributes.price).toFixed(2)})`,
          price: service.attributes.price,
        }));

      let matchedMountSize;
      if (shouldAutoMap) {
        const updateMountSize = lineItem?.attributes?.finishingService?.formattedDetailOne?.toLowerCase();
        matchedMountSize = mountSizes.find((mountSize) => mountSize?.value?.toLowerCase().includes(updateMountSize));
      }
      
      const autoSelectedMount = matchedMountSize || mountSizes[0];

      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          finishingServiceId: autoSelectedMount?.value,
          finishingServicePrice: autoSelectedMount?.price,
          requiredInputs: ['Mount Board Color'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          mountColor: defaultOptions.mountColor,
        },
      }));
    }
  },

  // Process finishing details data
  processFinishingDetailsData: (response) => {
    const { form, defaultOptions, setForm, lineItem, isEditInitializing } = get();
    const finishingServiceText = form.inputs.finishingService?.selectedValueText;
    const shouldAutoMap = lineItem && isEditInitializing;

    if (finishingServiceText === 'Framing') {
      const frameStains = response.data.map((detail) => ({
        value: detail.attributes.guid,
        text: detail.attributes.name,     
      }));
      
      // Only auto-map from lineItem during initial edit load
      let matchedFrameStain;
      if (shouldAutoMap && lineItem?.attributes?.finishingService?.finishingServiceDetailGuid) {
        const detailGuid = lineItem.attributes.finishingService.finishingServiceDetailGuid;
        matchedFrameStain = frameStains.find((stain) => stain.value === detailGuid);
      }
      
      // text matching if GUID matching didn't work (only during initial edit)
      if (!matchedFrameStain && shouldAutoMap && lineItem?.attributes?.finishingService?.finishingServiceDetail) {
        const updateFrameStain = lineItem.attributes.finishingService.finishingServiceDetail;
        matchedFrameStain = frameStains.find((stain) => stain.text === updateFrameStain);
      }
      
      if (!matchedFrameStain && shouldAutoMap && frameStains.length === 0) {
        matchedFrameStain = { value: 'none', text: 'None' };
        frameStains.push(matchedFrameStain);
      }

      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          requiredInputs: [
            ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
          frame: prevState.inputs.frame,
          frameMat: prevState.inputs.frameMat,
          acrylic: prevState.inputs.acrylic,
          ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ||
             prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' ||
             (prevState.inputs?.frameType?.selectedValueText === 'Desktop Frame' && frameStains.length > 0) ||
               (shouldAutoMap && lineItem?.attributes?.finishingService?.finishingCategory?.toLowerCase().includes('hardwood frame'))) && {
            frameStain: {
              ...defaultOptions.frameStain,
              ...(prevState.inputs.frameStain && !matchedFrameStain ? prevState.inputs.frameStain : {}),
              options: frameStains,
              isLoading: false,
              selectedValue: matchedFrameStain?.value ?? (frameStains.length > 0 ? prevState.inputs.frameStain?.selectedValue : undefined),
              selectedValueText: matchedFrameStain?.text ?? (frameStains.length > 0 ? prevState.inputs.frameStain?.selectedValueText : undefined),
              isDisabled: false,
            },
          }),
        },
      }));
    } else if (finishingServiceText === 'Mounting') {
      const mountServicePrice = form.meta.finishingServicePrice;
      const mountColors = response.data.map((detail) => ({
        value: detail.attributes.guid,
        text: detail.attributes.name,
        price: mountServicePrice,
      }));

      let matchedMountColor;
      if (shouldAutoMap) {
        matchedMountColor = mountColors?.find(
          (color) => color.text === lineItem?.attributes?.finishingService?.finishingServiceDetail
        );
      }

      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          finishingDetailId: matchedMountColor?.value,
          requiredInputs: matchedMountColor ? [] : ['Mount Board Color'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          mountColor: {
            ...defaultOptions.mountColor,
            options: mountColors,
            selectedValue: matchedMountColor?.value,
            selectedValueText: matchedMountColor?.text,
            selectedValuePrice: matchedMountColor?.price,
            isLoading: false,
            isDisabled: false,
          },
        },
      }));
    }
  },

  // Add line item to cart
  addLineItem: async (labOrderId, body) => {
    return httpClient.post(`lab_orders/${labOrderId}/line_items`, body);
  },

  // Add album line item to cart
  addAlbumLineItem: async (labOrderId, body) => {
    return httpClient.post(`lab_orders/${labOrderId}/albums`, body);
  },

  // Add multi-opening line item to cart
  // POST /api/v2/lab_orders/:lab_order_id/multi_openings_line_items
  addMultiOpeningLineItem: async (labOrderId, body) => {
    return httpClient.post(`lab_orders/${labOrderId}/multi_openings_line_items`, body);
  },

  // Update an existing multi-opening line item
  // PATCH /api/v2/lab_orders/:lab_order_id/multi_openings_line_items/:id
  updateMultiOpeningLineItem: async (labOrderId, lineItemId, body) => {
    return httpClient.patch(`lab_orders/${labOrderId}/multi_openings_line_items/${lineItemId}`, body);
  },

  // Update existing line item
  updateLineItem: async (labOrderId, lineItemId, body) => {
    return httpClient.patch(`lab_orders/${labOrderId}/line_items/${lineItemId}`, body);
  },

  // Process finishing service selection change
  processFinishingServiceChange: (selectedValueText) => {
    const { defaultOptions, setForm, lineItem, finishingCategoriesData, form, isEditInitializing } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    if (selectedValueText === 'Mounting') {
      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          finishingCategoryId: form.inputs.finishingService.selectedValue,
          requiredInputs: ['Mount Board Color'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
        },
      }));
      return;
    }

    if (selectedValueText === 'None') {
      setForm((prevState) => ({
        meta: {
          ...get().getBaseMeta(prevState),
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
          requiredInputs: [],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
        },
      }));
      return;
    }

    // Get finishing subcategories from stored data
    if (!finishingCategoriesData) return;

    const finishingSubcategories = finishingCategoriesData.data
      .filter(
        (finishingCategory) =>
          finishingCategory.attributes.finishingCategoryPrimaryName === selectedValueText,
      )
      .map((finishingCategory) => ({
        value: finishingCategory.attributes.guid,
        text: finishingCategory.attributes.finishingCategoryDetailName,
      }));

    let matchedSubCategory;
    if (shouldAutoMap) {
      let updateFinishingCategory = lineItem?.attributes?.finishingService?.finishingCategory?.toLowerCase();
      if (updateFinishingCategory?.includes('contemp')) {
        updateFinishingCategory = 'contemp';
      } else if (updateFinishingCategory?.includes('hard')) {
        updateFinishingCategory = 'hard';
      } else if (updateFinishingCategory?.includes('desk')) {
        updateFinishingCategory = 'desk';
      } else if (updateFinishingCategory?.includes('double') && updateFinishingCategory?.includes('4-ply')) {
        // Matches whether the active price list's category is named
        // "Double 4-Ply Mat" (old) or "Double Mat 4-Ply Mat" (new) - "double"
        // alone is a unique, order-independent key among the 3 matting
        // categories, so this works regardless of which naming is currently
        // being served.
        updateFinishingCategory = 'double';
      } else if (updateFinishingCategory?.includes('8-ply mat')) {
        updateFinishingCategory = '8-ply';
      } else if (updateFinishingCategory?.includes('4-ply mat')) {
        updateFinishingCategory = '4-ply';
      }
      matchedSubCategory = finishingSubcategories.find((finishingCategory) =>
        finishingCategory.text?.toLowerCase().includes(updateFinishingCategory)
      );
    }

    setForm((prevState) => {
      const newState = {
        meta: {
          ...get().getBaseMeta(prevState),
          finishingCategoryId: shouldAutoMap && matchedSubCategory ? matchedSubCategory.value : undefined,
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
        },
      };

      if (selectedValueText === 'Matting') {
        newState.meta.requiredInputs = ['Mat Format', 'Mat Size'];

        newState.inputs = {
          ...newState.inputs,
          matFormat: {
            ...defaultOptions.matFormat,
            options: finishingSubcategories,
            selectedValue: matchedSubCategory?.value,
            selectedValueText: matchedSubCategory?.text,
            isLoading: false,
            isDisabled: false,
          },
          matSize: {
            ...defaultOptions.matSize,
            isLoading: false,
            isDisabled: true,
          },
        };
      } else if (selectedValueText === 'Framing') {
        newState.meta.requiredInputs = ['Frame Name', 'Frame', 'Frame Mat', 'Acrylic',
          ...(prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ? ['Frame Stain'] : [])
        ];
        newState.inputs = {
          ...newState.inputs,
          frameType: {
            ...defaultOptions.frameType,
            options: finishingSubcategories,
            selectedValue: matchedSubCategory?.value,
            selectedValueText: matchedSubCategory?.text,
            isLoading: false,
            isDisabled: false,
          },
        };
      }
      return newState;
    });
  },

  // Process frame mats based on frame selection
  processFrameSelection: (frameSelectedValue, frameSelectedValueText) => {
    const { finishingServicesData, form, defaultOptions, setForm, lineItem, isEditInitializing } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    if (!finishingServicesData) return;

    // Use the text value for filtering, not the ID
    const frameText = frameSelectedValueText || form.inputs.frame?.selectedValueText;
    const frameTypeText = form.inputs.frameType?.selectedValueText;

    let frameMats;
    if (frameTypeText === 'Contemporary Frame') {
      frameMats = finishingServicesData.data
        .filter((service) => service.attributes.frameWoodPricing === frameText)
        .map((service) => service.attributes.frameMatName);
    } else if (frameTypeText === 'Hardwood Frame') {
      frameMats = finishingServicesData.data
        .filter((service) => service.attributes.frameWoodAndProfileName === frameText)
        .map((service) => service.attributes.frameMatName);
    } else if (frameTypeText === 'Desktop Frame') {
      frameMats = finishingServicesData.data
        .filter((service) => service.attributes.frameWoodPricing === frameText)
        .map((service) => service.attributes.frameMatName);
    } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
      frameMats = finishingServicesData.data
        .filter((service) => getFloatCanvasFrameName(service) === frameText)
        .map((service) => service.attributes.frameMatName);
    }

    const uniqueFrameMats = [...new Set(frameMats)];
    
    // Only auto-map from lineItem during initial edit load
    let matchedFrameMat;
    if (shouldAutoMap) {
      // try to find the exact service by GUID
      let matchingService;
      const serviceGuid = lineItem?.attributes?.finishingService?.finishingServiceGuid;
      
      if (serviceGuid) {
        matchingService = finishingServicesData.data.find(
          (service) => service.id === serviceGuid || service.attributes.guid === serviceGuid
        );
      }
      
      // GUID matching didn't work, fall back to frame text matching
      if (!matchingService) {
        if (frameTypeText === 'Contemporary Frame' || frameTypeText === 'Desktop Frame') {
          matchingService = finishingServicesData.data.find(
            (service) => service.attributes.frameWoodPricing === frameText
          );
        } else if (frameTypeText === 'Hardwood Frame') {
          matchingService = finishingServicesData.data.find(
            (service) => service.attributes.frameWoodAndProfileName === frameText
          );
        } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
          matchingService = finishingServicesData.data.find(
            (service) => getFloatCanvasFrameName(service) === frameText
          );
        }
      }
      
      // use frame_mat_name directly from the API response
      if (matchingService?.attributes?.frameMatName && matchingService.attributes.frameMatName.trim() !== '') {
        matchedFrameMat = matchingService.attributes.frameMatName;
      }
    }
    
    const sortedUniqueFrames = uniqueFrameMats.sort((a, b) => {
      if (a.toLowerCase() === 'no mat') return -1;
      if (b.toLowerCase() === 'no mat') return 1;
     
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    });

    

    // build options list and ensure 'None' is always available
    const frameMatOptions = sortedUniqueFrames.map((frameMat) => ({
      value: frameMat,
      text: frameMat,
    }));
    if (frameMatOptions.length === 0) {
      frameMatOptions.push({ value: 'None', text: 'None' });
    }

    setForm((prevState) => ({
      meta: {
        ...get().getBaseMeta(prevState),
        requiredInputs: [
          'Frame Mat',
          'Acrylic',
          ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ||
            form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame'
          ) ? ['Frame Stain'] : []),
        ],
      },
      inputs: {
        paperType: prevState.inputs.paperType,
        printSize: prevState.inputs.printSize,
        finishingService: prevState.inputs.finishingService,
        frameType: prevState.inputs.frameType,
        frame: prevState.inputs.frame,
        frameMat: {
          ...defaultOptions.frameMat,
          options: frameMatOptions,
          selectedValueText: matchedFrameMat,
          selectedValue: matchedFrameMat,
          isLoading: false,
          isDisabled: false,
        },
        acrylic: defaultOptions.acrylic,
        ...((form.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ||
           form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame'
        ) && {
          ...((form.inputs?.frameType?.selectedValueText === 'Hardwood Frame' ||
             form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame'
          ) && {
            frameStain: prevState.inputs.frameStain 
          }),
        }),
      },
    }));

    // frame mat is set and we're in initial edit, trigger processFrameMatSelection
    if (shouldAutoMap && matchedFrameMat) {
      setTimeout(() => {
        const { processFrameMatSelection } = get();
        processFrameMatSelection(matchedFrameMat, matchedFrameMat);
      }, 0);
    }
  },

  // restore mat type / opening shape (frame_mat_pricing) from an existing line item during edit
  processFrameMatPricingData: (response) => {
    const { setForm, lineItem, isEditInitializing, getBaseMeta } = get();
    const shouldAutoMap = lineItem && isEditInitializing;
    if (!shouldAutoMap) return;

    const existingFrameMat = lineItem?.attributes?.frameMat;
    if (!existingFrameMat) return;

    const rows = response?.data || [];
    const matchedRow =
      rows.find((row) => row.id === existingFrameMat.guid) ||
      rows.find(
        (row) =>
          row.attributes.matType === existingFrameMat.matType &&
          row.attributes.openingShape === existingFrameMat.openingShape,
      );
    if (!matchedRow) return;

    setForm((prevState) => {
      const openingShapeLabels = { standard: 'Standard', circle: 'Circle', oval: 'Oval' };
      const openingShape = matchedRow.attributes.openingShape;

      return {
        meta: {
          ...getBaseMeta(prevState),
          matType: matchedRow.attributes.matType,
          matOpeningShape: openingShape,
          frameMatPricingGuid: matchedRow.id,

          requiredInputs: (prevState.meta.requiredInputs || []).filter(
            (label) => label !== 'Mat Opening Shape' && label !== 'Mat Type',
          ),
        },

        inputs: prevState.inputs?.matOpeningShape
          ? {
              ...prevState.inputs,
              matOpeningShape: {
                ...prevState.inputs.matOpeningShape,
                selectedValue: openingShape,
                selectedValueText: openingShapeLabels[openingShape] || openingShape,
              },
            }
          : prevState.inputs,
      };
    });
  },

  // process acrylics based on frame mat selection
  processFrameMatSelection: (frameMatSelectedValue, frameMatSelectedValueText) => {
    const { finishingServicesData, form, defaultOptions, setForm, lineItem, isEditInitializing } = get();
    const shouldAutoMap = lineItem && isEditInitializing;

    if (!finishingServicesData) return;

    // use the text value for filtering, not the ID
    const frameMatText = frameMatSelectedValueText || form.inputs.frameMat?.selectedValueText;
    // Circle/Oval Frame Mat stores the products/frame_mats mat_type key
    // ('8-ply' / 'double-mat-4-ply') as its value, not a color name - see
    // deriveCircleOvalMatTypeKey.
    const frameMatValue = frameMatSelectedValue || form.inputs.frameMat?.selectedValue;
    const frameText = form.inputs.frame?.selectedValueText;
    const frameTypeText = form.inputs.frameType?.selectedValueText;

    // helper function to check if frameMat matches
    const frameMatMatches = (serviceFrameMatName) => {
      if (frameMatText === 'None') {
        return !serviceFrameMatName || serviceFrameMatName.trim() === '';
      }
      return serviceFrameMatName === frameMatText;
    };

    // Circle/Oval matches on the derived mat_type key rather than the raw
    // compound name text (see CIRCLE_OVAL_MAT_TYPE_PATTERNS above).
    const circleOvalFrameMatMatches = (service) =>
      deriveCircleOvalMatTypeKey(getCircleOvalServiceMatNameSuffix(service)) === frameMatValue;

    let acrylics;
    if (frameTypeText === 'Contemporary Frame') {
      acrylics = finishingServicesData.data
        .filter(
          (service) =>
            service.attributes.frameWoodPricing === frameText &&
            frameMatMatches(service.attributes.frameMatName),
        )
        .map((service) => ({
          value: service.attributes.guid,
          text: service.attributes.frameGlassName || '',
          price: service.attributes.price,
        }));
    } else if (frameTypeText === 'Hardwood Frame') {
      acrylics = finishingServicesData.data
        .filter(
          (service) =>
            service.attributes.frameWoodAndProfileName === frameText &&
            frameMatMatches(service.attributes.frameMatName),
        )
        .map((service) => ({
          value: service.attributes.guid,
          text: service.attributes.frameGlassName || '',
          price: service.attributes.price,
        }));
    } else if (frameTypeText === 'Desktop Frame') {
      acrylics = finishingServicesData.data
        .filter(
          (service) =>
            service.attributes.frameWoodPricing === frameText &&
            frameMatMatches(service.attributes.frameMatName),
        )
        .map((service) => ({
          value: service.attributes.guid,
          text: service.attributes.frameGlassName || '',
          price: service.attributes.price,
        }));
    } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
      acrylics = finishingServicesData.data
        .filter(
          (service) =>
            getFloatCanvasFrameName(service) === frameText &&
            frameMatMatches(service.attributes.frameMatName),
        )
        .map((service) => ({
          value: service.attributes.guid,
          text: service.attributes.frameGlassName || '',
          price: service.attributes.price,
        }));
    } else if (isCircleOrOvalFrameType(frameTypeText)) {
      // Circle/Oval frame types: no Frame Name dimension, so filter on mat type only.
      acrylics = finishingServicesData.data
        .filter(circleOvalFrameMatMatches)
        .map((service) => ({
          value: service.attributes.guid,
          text: service.attributes.frameGlassName || '',
          price: service.attributes.price,
        }));
    }

    // Only auto-map from lineItem during initial edit load
    let matchedAcrylics;
    if (shouldAutoMap) {
      // try to find the exact service by GUID
      let matchingService;
      const serviceGuid = lineItem?.attributes?.finishingService?.finishingServiceGuid;
      
      if (serviceGuid) {
        matchingService = finishingServicesData.data.find(
          (service) => service.id === serviceGuid || service.attributes.guid === serviceGuid
        );
      }
      
      // GUID matching didn't work, fall back to frame + frameMat matching
      if (!matchingService) {
        if (frameTypeText === 'Contemporary Frame' || frameTypeText === 'Desktop Frame') {
          matchingService = finishingServicesData.data.find(
            (service) =>
              service.attributes.frameWoodPricing === frameText &&
              frameMatMatches(service.attributes.frameMatName)
          );
        } else if (frameTypeText === 'Hardwood Frame') {
          matchingService = finishingServicesData.data.find(
            (service) =>
              service.attributes.frameWoodAndProfileName === frameText &&
              frameMatMatches(service.attributes.frameMatName)
          );
        } else if (frameTypeText === 'Hardwood Float Canvas Frame') {
          matchingService = finishingServicesData.data.find(
            (service) =>
              getFloatCanvasFrameName(service) === frameText &&
              frameMatMatches(service.attributes.frameMatName)
          );
        } else if (isCircleOrOvalFrameType(frameTypeText)) {
          matchingService = finishingServicesData.data.find(circleOvalFrameMatMatches);
        }
      }
      
      // use frame_glass_name directly from the API response
      if (matchingService?.attributes?.frameGlassName) {
        const glassName = matchingService.attributes.frameGlassName;
        matchedAcrylics = acrylics?.find((acrylic) => acrylic.text === glassName);
      }
    }

    const filteredAcrylics = (acrylics || []).reduce((acc, acrylic) => {
      if (!acrylic?.text || acrylic.text.trim() === '' || !acrylic?.value) return acc;
      const key = acrylic.text.trim().toLowerCase();
      if (acc.some((existing) => existing.text.trim().toLowerCase() === key)) return acc;
      acc.push(acrylic);
      return acc;
    }, []);

    if(!matchedAcrylics && shouldAutoMap && filteredAcrylics.length === 0) {
      matchedAcrylics = {value: 'none', text: 'None'};
      filteredAcrylics.push(matchedAcrylics);
    }

    setForm((prevState) => ({
      meta: {
        ...get().getBaseMeta(prevState),
        finishingServiceId: matchedAcrylics?.value ? matchedAcrylics.value : prevState.meta.finishingServiceId,
        requiredInputs: ['Acrylic',
          ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
            || prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' )
            ? ['Frame Stain'] : [])
        ],
      },
      inputs: {
        paperType: prevState.inputs.paperType,
        printSize: prevState.inputs.printSize,
        finishingService: prevState.inputs.finishingService,
        frameType: prevState.inputs.frameType,
        frame: prevState.inputs.frame,
        frameMat: prevState.inputs.frameMat,
        acrylic: {
          ...defaultOptions.acrylic,
          options: filteredAcrylics,
          isLoading: false,
          isDisabled: false,
          selectedValue: matchedAcrylics?.value,
          selectedValueText: matchedAcrylics?.text,
          selectedValuePrice: matchedAcrylics?.price
        },
        ...((form.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
          || form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame')
          && { frameStain: prevState.inputs.frameStain || defaultOptions.frameStain }),
      },
    }));
  },

  // update photos with cart icons
  updatePhotosWithCartIcons: (imagesInCart) => {
    const { photos } = get();
    const photosWithCartIcon = photos.map((photo) => {
      if (imagesInCart.includes(photo.attributes.guid)) {
        return {
          ...photo,
          attributes: {
            ...photo.attributes,
            inCart: true,
            isAddedToCart: true,
          },
        };
      }
      return photo;
    });
    set({ photos: photosWithCartIcon });
  },

  // calculate subtotal from form inputs
  calculateSubtotal: () => {
    const { form } = get();
    if (!form) return '0.00';

    const inputs = [
      form.inputs.printSize?.selectedValuePrice,
      form.inputs.mountSize?.selectedValuePrice,
      form.inputs.matSize?.selectedValuePrice,
      form.inputs.frameType?.selectedValuePrice,
      form.inputs.frame?.selectedValuePrice,
      form.inputs.frameMat?.selectedValuePrice,
      form.inputs.acrylic?.selectedValuePrice,
      form.inputs.frameStain?.selectedValuePrice,
    ];

    let sum = 0;
    inputs.forEach((input) => {
      const floatValue = parseFloat(input);
      if (!Number.isNaN(floatValue)) {
        sum = Math.max(sum, floatValue);
      }
    });

    const acrylicPrice = parseFloat(form.inputs.acrylic?.selectedValuePrice);
    if (acrylicPrice) {
      return acrylicPrice.toFixed(2);
    }
    return sum.toFixed(2);
  },

    }),
    {
      name: 'product-selection-store',
      partialize: (state) => ({
        photos: state.photos,
        productType: state.productType,
        orderId: state.orderId,
      }),
    }
  )
);

export default useProductSelectionStore;
